import {
  StateInspector,
  Timeline,
  MemoryMonitor,
  PerformanceMetrics,
  ComponentViewer,
} from '@/components/timeline';
import NetworkDashboard from '@/components/network/NetworkDashboard';
import MetricsDashboard from '@/components/dashboard/MetricsDashboard';
import { useDebugTimelineMachine } from '@/components/hooks/useDebugTimelineMachine';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Collapsible, CollapsibleContent, CollapsibleTrigger } from 'debug-time-machine-ui';
import {
  Activity,
  Clock,
  BarChart3,
  Component,
  Zap,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Settings,
  Share2,
  AlertCircle,
  CheckCircle2,
  Network,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Star,
  X,
} from 'lucide-react';

export function DebugTimeMachine() {
  // 모든 상태와 로직이 커스텀 훅으로 이동
  const {
    // 서버 연결 상태
    isConnected,
    connectedClients,

    // 이벤트 데이터
    timelineEvents,
    filteredEvents,

    // UI 상태
    currentTime,
    selectedEvent,
    filter,
    isPlaying,
    activeTab,
    sidebarCollapsed,
    stateInspectorOpen,
    memoryMonitorOpen,

    // 네트워크 관련
    networkRequests,
    isNetworkRecording,

    // 액션 함수들
    jumpToTime,
    jumpToPrevEvent,
    jumpToNextEvent,
    shareSnapshot,
    toggleBookmark,

    // 상태 업데이트 함수들
    setFilter,
    setIsPlaying,
    setSelectedEvent,
    setActiveTab,
    setSidebarCollapsed,
    setStateInspectorOpen,
    setMemoryMonitorOpen,

    // 네트워크 액션 함수들
    handleToggleNetworkRecording,
    handleClearNetworkHistory,
    handleExportNetworkData,

    // 메모리 관리 함수들
    updateEvents,
    clearEvents,

    // 유틸리티 함수들
    getFilterCount,
    getStatusColor,
  } = useDebugTimelineMachine({ demoMode: false }); // 데모 모드 비활성화

  console.log('DebugTimeMachine render:', {
    activeTab,
    timelineEventsCount: timelineEvents.length,
    isConnected,
    networkRequestsCount: networkRequests.length,
  });

  // 탭 변경 핸들러에 로깅 추가
  const handleTabChange = (value: string) => {
    console.log('Tab changing from', activeTab, 'to', value);
    setActiveTab(value as 'timeline' | 'metrics' | 'components' | 'advanced');
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800'>
      {/* Modern Header */}
      <header className='bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-700 sticky top-0 z-50'>
        <div className='px-6 py-4'>
          <div className='flex items-center justify-between'>
            {/* Logo & Status */}
            <div className='flex items-center gap-4'>
              <div className='flex items-center gap-3'>
                <div className='relative'>
                  <Activity className='h-8 w-8 text-blue-600 dark:text-blue-400' />
                  <div
                    className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${getStatusColor()}`}
                  />
                </div>
                <div>
                  <h1 className='text-xl font-bold text-slate-900 dark:text-white'>
                    Debug Time Machine
                  </h1>
                  <div className='flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400'>
                    {isConnected ? (
                      <>
                        <CheckCircle2 className='h-3 w-3 text-green-500' /> Connected
                      </>
                    ) : (
                      <>
                        <AlertCircle className='h-3 w-3 text-red-500' /> Disconnected
                      </>
                    )}
                    <span>•</span>
                    <span>{timelineEvents.length} events</span>
                    <span>•</span>
                    <span className='text-xs'>({connectedClients.length} clients)</span>
                    {timelineEvents.length === 0 && (
                      <>
                        <span>•</span>
                        <span className='text-amber-600 dark:text-amber-400'>
                          Waiting for demo app events
                        </span>
                        <span>•</span>
                        <span className='text-xs text-slate-500'>Demo: http://localhost:3000</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className='flex items-center gap-4'>
              <div className='flex gap-2'>
                <Badge
                  variant={filter === 'errors' ? 'destructive' : 'secondary'}
                  className='cursor-pointer'
                  onClick={() => setFilter(filter === 'errors' ? 'all' : 'errors')}
                >
                  {getFilterCount('errors')} Errors
                </Badge>
                <Badge
                  variant={filter === 'api' ? 'default' : 'secondary'}
                  className='cursor-pointer'
                  onClick={() => setFilter(filter === 'api' ? 'all' : 'api')}
                >
                  {getFilterCount('api')} API
                </Badge>
                <Badge
                  variant={filter === 'states' ? 'default' : 'secondary'}
                  className='cursor-pointer'
                  onClick={() => setFilter(filter === 'states' ? 'all' : 'states')}
                >
                  {getFilterCount('states')} States
                </Badge>
              </div>

              <Button variant='outline' size='sm' onClick={shareSnapshot}>
                <Share2 className='h-4 w-4' />
                Share
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className='flex h-[calc(100vh-88px)]'>
        {/* Main Panel */}
        <div className='flex-1 flex flex-col'>
          {/* Content Tabs */}
          <div className='flex-1 flex flex-col'>
            <div className='px-6 py-3 bg-white/50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700'>
              <div className='flex gap-1'>
                <button
                  onClick={() => handleTabChange('timeline')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                    activeTab === 'timeline'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Clock className='h-4 w-4' />
                  Timeline
                </button>
                <button
                  onClick={() => handleTabChange('metrics')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                    activeTab === 'metrics'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <BarChart3 className='h-4 w-4' />
                  Metrics
                </button>
                <button
                  onClick={() => handleTabChange('components')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                    activeTab === 'components'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Component className='h-4 w-4' />
                  Components
                </button>
                <button
                  onClick={() => handleTabChange('advanced')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                    activeTab === 'advanced'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Zap className='h-4 w-4' />
                  Advanced
                </button>
              </div>
            </div>

            {/* Tab Contents - 조건부 렌더링으로 변경 */}
            <div className='flex-1 overflow-hidden'>
              {activeTab === 'timeline' && (
                <div className='h-full flex flex-col'>
                  {/* Timeline - 상단 20% */}
                  <div className='h-1/5 p-4 pb-2'>
                    <Card className='h-full'>
                      <CardHeader className='pb-2 px-4 py-3'>
                        <CardTitle className='flex items-center gap-2 text-sm'>
                          <Clock className='h-4 w-4' />
                          Event Timeline
                          {/* 재생 컨트롤 추가 */}
                          <div className='ml-auto flex items-center gap-2'>
                            <Button variant='outline' size='sm' onClick={jumpToPrevEvent}>
                              <SkipBack className='h-3 w-3' />
                            </Button>
                            <Button variant='outline' size='sm' onClick={() => setIsPlaying(!isPlaying)}>
                              {isPlaying ? <Pause className='h-3 w-3' /> : <Play className='h-3 w-3' />}
                            </Button>
                            <Button variant='outline' size='sm' onClick={jumpToNextEvent}>
                              <SkipForward className='h-3 w-3' />
                            </Button>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='h-[calc(100%-56px)] px-4 pb-4'>
                        <Timeline
                          timelineEvents={timelineEvents}
                          filteredEvents={filteredEvents}
                          currentTime={currentTime}
                          jumpToTime={jumpToTime}
                          onEventSelect={setSelectedEvent}
                          isConnected={isConnected}
                        />
                      </CardContent>
                    </Card>
                  </div>

                  {/* 메인 컨텐츠 영역 - 하단 80% */}
                  <div className='flex-1 flex gap-4 p-4 pt-2 min-h-0'>
                    {/* 좌측 Event 영역 (50%) */}
                    <div className='w-1/2 flex flex-col gap-3 min-h-0'>
                      {/* Event List (상단) */}
                      <div className='flex-1 min-h-0'>
                        <Card className='h-full'>
                          <CardHeader className='pb-2 px-4 py-3'>
                            <CardTitle className='flex items-center justify-between text-sm'>
                              <div className='flex items-center gap-2'>
                                <Activity className='h-4 w-4' />
                                Event List
                              </div>
                              <Badge variant='secondary' className='text-xs'>
                                {filteredEvents.length} events
                              </Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className='h-[calc(100%-56px)] px-4 pb-4 overflow-y-auto'>
                            {filteredEvents.length > 0 ? (
                              <div className='space-y-1'>
                                {filteredEvents.map((event) => (
                                  <button
                                    key={event.id}
                                    onClick={() => {
                                      setSelectedEvent(event);
                                      jumpToTime(event.timestamp);
                                    }}
                                    className={`group w-full text-left p-2 rounded-lg border transition-all hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                                      selectedEvent?.id === event.id
                                        ? 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800'
                                        : 'border-slate-200 dark:border-slate-700'
                                    }`}
                                  >
                                    <div className='flex items-center gap-2 mb-1'>
                                      {/* Event Type Icon */}
                                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                        event.isError || event.severity === 'critical' ? 'bg-red-500' :
                                        event.severity === 'warning' ? 'bg-amber-500' :
                                        event.type === 'api_call' || event.type === 'API_CALL' ? 'bg-violet-500' :
                                        event.type === 'state_change' || event.type === 'STATE_CHANGE' ? 'bg-emerald-500' :
                                        event.type === 'action' || event.type === 'USER_ACTION' ? 'bg-blue-500' :
                                        'bg-slate-500'
                                      }`} />
                                      <span className='text-xs font-mono text-slate-500 dark:text-slate-400'>
                                        {new Date(event.timestamp).toLocaleTimeString()}
                                      </span>
                                      <Badge variant='outline' className='text-xs h-4'>
                                        {event.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                      </Badge>
                                      {event.isError && (
                                        <AlertTriangle className='h-3 w-3 text-red-500' />
                                      )}
                                      {event.isBookmarked && (
                                        <Star className='h-3 w-3 text-amber-500 fill-current' />
                                      )}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleBookmark(event.id);
                                        }}
                                        className='ml-auto h-4 w-4 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-200 dark:hover:bg-slate-700 rounded'
                                      >
                                        <Star className={`h-3 w-3 ${event.isBookmarked ? 'text-amber-500 fill-current' : 'text-slate-400'}`} />
                                      </button>
                                    </div>
                                    <div className='text-sm text-slate-900 dark:text-white font-medium'>
                                      {event.title}
                                    </div>
                                    {event.description && (
                                      <div className='text-xs text-slate-600 dark:text-slate-400 mt-1 line-clamp-2'>
                                        {event.description}
                                      </div>
                                    )}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className='text-center py-8'>
                                <Activity className='w-8 h-8 mx-auto mb-2 text-slate-400' />
                                <p className='text-sm text-slate-500 dark:text-slate-400'>
                                  No events to display
                                </p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>

                      {/* Selected Event Detail (하단 - 아코디언) */}
                      <div className='flex-none'>
                        <Card>
                          <CardHeader className='pb-2 px-4 py-3'>
                            <CardTitle className='flex items-center gap-2 text-sm'>
                              <AlertCircle className='h-4 w-4' />
                              Event Details
                              {selectedEvent && (
                                <Badge variant='outline' className='text-xs h-4'>
                                  {selectedEvent.type}
                                </Badge>
                              )}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className='max-h-48 overflow-y-auto px-4 pb-4'>
                            {selectedEvent ? (
                              <div className='space-y-3'>
                                <div className='grid grid-cols-2 gap-4 text-sm'>
                                  <div>
                                    <span className='font-medium text-slate-600 dark:text-slate-400'>Type:</span>
                                    <div className='mt-1'>
                                      <Badge variant='outline' className='text-xs'>
                                        {selectedEvent.type}
                                      </Badge>
                                    </div>
                                  </div>
                                  <div>
                                    <span className='font-medium text-slate-600 dark:text-slate-400'>Time:</span>
                                    <div className='mt-1 font-mono text-sm'>
                                      {new Date(selectedEvent.timestamp).toLocaleTimeString()}
                                    </div>
                                  </div>
                                </div>

                                {selectedEvent.description && (
                                  <div>
                                    <span className='font-medium text-slate-600 dark:text-slate-400 text-sm'>Message:</span>
                                    <div className='mt-1 p-3 bg-slate-50 dark:bg-slate-800 rounded text-sm font-mono leading-relaxed'>
                                      {selectedEvent.description}
                                    </div>
                                  </div>
                                )}

                                {selectedEvent.data?.stack && (
                                  <Collapsible>
                                    <CollapsibleTrigger asChild>
                                      <Button variant='ghost' size='sm' className='h-auto p-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'>
                                        <ChevronRight className='h-4 w-4 mr-1 transform transition-transform ui-state-open:rotate-90' />
                                        Stack Trace ({selectedEvent.data.stack.split('\n').length} frames)
                                      </Button>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      <div className='mt-2 p-3 bg-red-50 dark:bg-red-950/30 rounded text-sm font-mono leading-relaxed border border-red-200 dark:border-red-800/50 max-h-32 overflow-y-auto'>
                                        <pre className='whitespace-pre-wrap'>{selectedEvent.data.stack}</pre>
                                      </div>
                                    </CollapsibleContent>
                                  </Collapsible>
                                )}

                                {selectedEvent.severity === 'critical' && selectedEvent.suggestion && (
                                  <div className='p-3 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200 dark:border-amber-800/50'>
                                    <div className='flex items-start gap-2'>
                                      <AlertTriangle className='h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0' />
                                      <div className='text-sm text-amber-700 dark:text-amber-300'>
                                        <span className='font-medium'>Suggestion:</span>
                                        <div className='mt-1'>{selectedEvent.suggestion}</div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className='text-center py-4 text-sm text-slate-500 dark:text-slate-400'>
                                Select an event to view details
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </div>

                    {/* 우측 모니터링 영역 (50%) */}
                    <div className='w-1/2 flex flex-col gap-3 min-h-0'>
                      {/* State Inspector - 항상 위치 유지 */}
                      <div 
                        className='min-h-0 panel-transition overflow-hidden'
                        style={{
                          height: stateInspectorOpen 
                            ? memoryMonitorOpen 
                              ? 'calc(60% - 0.375rem)' // 60% - half gap
                              : 'calc(100% - 2.75rem)' // 전체에서 닫힌 패널 헤더 제외
                            : '2.5rem' // 헤더만
                        }}>
                        <Card className='h-full overflow-hidden'>
                          <CardHeader className='pb-2 px-4 py-3 flex-shrink-0 border-b border-border/50 flex items-center min-h-[2.5rem]'>
                            <CardTitle className='flex items-center justify-between text-sm w-full'>
                              <button
                                onClick={() => setStateInspectorOpen(!stateInspectorOpen)}
                                className='flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded p-1 -ml-1 flex-1 text-left transition-colors'
                              >
                                <Settings className='h-4 w-4' />
                                State Inspector
                                <ChevronDown className={`h-3 w-3 ml-auto chevron-transition ${stateInspectorOpen ? 'rotate-0' : '-rotate-90'}`} />
                              </button>
                              {stateInspectorOpen && selectedEvent && (
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  onClick={() => toggleBookmark(selectedEvent.id)}
                                  className='h-6 w-6 p-0 ml-2 flex-shrink-0'
                                >
                                  <Star className={`h-3 w-3 ${selectedEvent.isBookmarked ? 'text-amber-500 fill-current' : 'text-slate-400'}`} />
                                </Button>
                              )}
                            </CardTitle>
                          </CardHeader>
                          {stateInspectorOpen && (
                            <CardContent className='p-4 h-full overflow-hidden' style={{ height: 'calc(100% - 4rem)' }}>
                              <div className='h-full overflow-y-auto'>
                                <StateInspector
                                  selectedEvent={selectedEvent}
                                  currentTime={currentTime}
                                  toggleBookmark={toggleBookmark}
                                />
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      </div>

                      {/* Memory Monitor - 항상 위치 유지 */}
                      <div 
                        className='min-h-0 panel-transition overflow-hidden'
                        style={{
                          height: memoryMonitorOpen 
                            ? stateInspectorOpen 
                              ? 'calc(40% - 0.375rem)' // 40% - half gap
                              : 'calc(100% - 2.75rem)' // 전체에서 닫힌 패널 헤더 제외
                            : '2.5rem' // 헤더만
                        }}>
                        <Card className='h-full overflow-hidden'>
                          <CardHeader className='pb-2 px-4 py-3 flex-shrink-0 border-b border-border/50 flex items-center min-h-[2.5rem]'>
                            <CardTitle className='flex items-center justify-between text-sm w-full'>
                              <button
                                onClick={() => setMemoryMonitorOpen(!memoryMonitorOpen)}
                                className='flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded p-1 -ml-1 flex-1 text-left transition-colors'
                              >
                                <Activity className='h-4 w-4' />
                                Memory Monitor
                                <ChevronDown className={`h-3 w-3 ml-auto chevron-transition ${memoryMonitorOpen ? 'rotate-0' : '-rotate-90'}`} />
                              </button>
                            </CardTitle>
                          </CardHeader>
                          {memoryMonitorOpen && (
                            <CardContent className='p-4 h-full overflow-hidden' style={{ height: 'calc(100% - 4rem)' }}>
                              <div className='h-full overflow-y-auto'>
                                <MemoryMonitor
                                  events={timelineEvents}
                                  onEventsUpdated={updateEvents}
                                  onManualCleanup={clearEvents}
                                  className='h-full'
                                  compact={true}
                                />
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'metrics' && (
                <div className='h-full p-6'>
                  <Card className='h-full'>
                    <CardHeader className='pb-3'>
                      <CardTitle className='flex items-center gap-2'>
                        <BarChart3 className='h-5 w-5' />
                        Performance Metrics
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='h-[calc(100%-80px)] overflow-y-auto'>
                      {timelineEvents.length > 0 ? (
                        <PerformanceMetrics events={timelineEvents} />
                      ) : (
                        <div className='text-center py-8'>
                          <BarChart3 className='w-12 h-12 mx-auto mb-4 text-slate-400' />
                          <h3 className='text-lg font-medium text-slate-600 dark:text-slate-300 mb-2'>
                            No Metrics Data
                          </h3>
                          <p className='text-sm text-slate-500 dark:text-slate-400'>
                            Start interacting with the demo app to generate performance metrics.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {activeTab === 'components' && (
                <div className='h-full p-6'>
                  <Card className='h-full'>
                    <CardHeader className='pb-3'>
                      <CardTitle className='flex items-center gap-2'>
                        <Component className='h-5 w-5' />
                        Component Tree
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='h-[calc(100%-80px)] overflow-y-auto'>
                      {timelineEvents.length > 0 ? (
                        <ComponentViewer events={timelineEvents} />
                      ) : (
                        <div className='text-center py-8'>
                          <Component className='w-12 h-12 mx-auto mb-4 text-slate-400' />
                          <h3 className='text-lg font-medium text-slate-600 dark:text-slate-300 mb-2'>
                            No Component Data
                          </h3>
                          <p className='text-sm text-slate-500 dark:text-slate-400'>
                            Start interacting with the demo app to see component activity.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {activeTab === 'advanced' && (
                <div className='h-full p-6'>
                  <div className='space-y-6'>
                    <Card className='h-auto'>
                      <CardHeader className='pb-3'>
                        <CardTitle className='flex items-center gap-2'>
                          <Zap className='h-5 w-5' />
                          Advanced Analytics
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {isConnected ? (
                          <MetricsDashboard
                            isConnected={isConnected}
                            clientId={timelineEvents.length > 0 ? 'frontend-dashboard' : null}
                          />
                        ) : (
                          <div className='text-center py-8'>
                            <Zap className='w-12 h-12 mx-auto mb-4 text-slate-400' />
                            <h3 className='text-lg font-medium text-slate-600 dark:text-slate-300 mb-2'>
                              No Connection
                            </h3>
                            <p className='text-sm text-slate-500 dark:text-slate-400'>
                              Connect to the debug server to view advanced analytics.
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card className='h-auto'>
                      <CardHeader className='pb-3'>
                        <CardTitle className='flex items-center gap-2'>
                          <Network className='h-5 w-5' />
                          Network Monitoring
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <NetworkDashboard
                          requests={networkRequests}
                          isRecording={isNetworkRecording}
                          onToggleRecording={handleToggleNetworkRecording}
                          onClearHistory={handleClearNetworkHistory}
                          onExportData={handleExportNetworkData}
                        />
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
